import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  makeStyles,
  Option,
  Spinner,
  tokens
} from "@fluentui/react-components";
import { Dismiss24Regular, ErrorCircle12Filled } from "@fluentui/react-icons";
import { FormattedMessage, useIntl } from "react-intl";
import {
  DEFAULT_SUMMARY_TIMEFRAME_PRESET,
  SUMMARY_TIMEFRAME_PRESETS,
  buildSummaryTimeframe,
  getDefaultCustomTimeframe,
  toDatetimeLocalValue
} from "./summary-timeframe-utils";
import { DEFAULT_SUMMARY_DATA_SOURCES, sanitizeSummaryDataSources } from "../../utils/room-persistence-utils";

const useStyles = makeStyles({
  dialog: {
    boxSizing: "border-box",
    maxWidth: "520px",
    minWidth: "0",
    width: "calc(100vw - 32px)"
  },
  timeframeField: {
    marginBottom: "16px"
  },
  customFields: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))",
    gap: "12px",
    marginBottom: "12px",
    "@media (max-width: 480px)": {
      gridTemplateColumns: "1fr"
    }
  },
  customDateTimeField: {
    minWidth: "0"
  },
  fullWidthControl: {
    boxSizing: "border-box",
    maxWidth: "100%",
    minWidth: "0",
    width: "100%"
  },
  dateTimeInput: {
    boxSizing: "border-box",
    maxWidth: "100%",
    minWidth: "0",
    width: "100%",
    "& input": {
      minWidth: "0",
      width: "100%"
    },
    "& input::-webkit-calendar-picker-indicator": {
      filter: "invert(1) brightness(2)",
      opacity: 1
    }
  },
  validationMessage: {
    alignItems: "center",
    color: tokens.colorPaletteRedForeground1,
    columnGap: "4px",
    display: "flex",
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    marginBottom: "12px"
  },
  validationIcon: {
    display: "block",
    flexShrink: 0,
    height: "12px",
    lineHeight: "0",
    width: "12px"
  }
});

const DEFAULT_SUMMARY_DOWNLOAD_FORMAT = "pdf";
const SUMMARY_DOWNLOAD_FORMATS = [{ id: "pdf" }, { id: "md" }, { id: "txt" }];

function dataSourceLabel(intl, sourceId) {
  switch (sourceId) {
    case "private_chat":
      return intl.formatMessage({ id: "summary-dialog.data-source.private-chat", defaultMessage: "Private Chat" });
    case "agent_chat":
      return intl.formatMessage({ id: "summary-dialog.data-source.agent-chat", defaultMessage: "Agent Chat" });
    case "transcription":
      return intl.formatMessage({ id: "summary-dialog.data-source.transcription", defaultMessage: "Transcription" });
    case "chat":
    default:
      return intl.formatMessage({ id: "summary-dialog.data-source.chat", defaultMessage: "Chat" });
  }
}

function dataSourceMessage(sourceId) {
  switch (sourceId) {
    case "private_chat":
      return <FormattedMessage id="summary-dialog.data-source.private-chat" defaultMessage="Private Chat" />;
    case "agent_chat":
      return <FormattedMessage id="summary-dialog.data-source.agent-chat" defaultMessage="Agent Chat" />;
    case "transcription":
      return <FormattedMessage id="summary-dialog.data-source.transcription" defaultMessage="Transcription" />;
    case "chat":
    default:
      return <FormattedMessage id="summary-dialog.data-source.chat" defaultMessage="Chat" />;
  }
}

function presetLabel(intl, presetId) {
  switch (presetId) {
    case "last-15":
      return intl.formatMessage({ id: "summary-dialog.timeframe.last-15", defaultMessage: "Last 15 min" });
    case "last-60":
      return intl.formatMessage({ id: "summary-dialog.timeframe.last-60", defaultMessage: "Last 1 hour" });
    case "last-180":
      return intl.formatMessage({ id: "summary-dialog.timeframe.last-180", defaultMessage: "Last 3 hours" });
    case "last-1440":
      return intl.formatMessage({ id: "summary-dialog.timeframe.last-1440", defaultMessage: "Last 24 hours" });
    case "custom":
      return intl.formatMessage({ id: "summary-dialog.timeframe.custom", defaultMessage: "Custom" });
    case "last-30":
    default:
      return intl.formatMessage({ id: "summary-dialog.timeframe.last-30", defaultMessage: "Last 30 min" });
  }
}

function presetMessage(presetId) {
  switch (presetId) {
    case "last-15":
      return <FormattedMessage id="summary-dialog.timeframe.last-15" defaultMessage="Last 15 min" />;
    case "last-60":
      return <FormattedMessage id="summary-dialog.timeframe.last-60" defaultMessage="Last 1 hour" />;
    case "last-180":
      return <FormattedMessage id="summary-dialog.timeframe.last-180" defaultMessage="Last 3 hours" />;
    case "last-1440":
      return <FormattedMessage id="summary-dialog.timeframe.last-1440" defaultMessage="Last 24 hours" />;
    case "custom":
      return <FormattedMessage id="summary-dialog.timeframe.custom" defaultMessage="Custom" />;
    case "last-30":
    default:
      return <FormattedMessage id="summary-dialog.timeframe.last-30" defaultMessage="Last 30 min" />;
  }
}

function downloadFormatLabel(intl, formatId) {
  switch (formatId) {
    case "md":
      return intl.formatMessage({ id: "summary-dialog.format.markdown", defaultMessage: "Markdown" });
    case "txt":
      return intl.formatMessage({ id: "summary-dialog.format.text", defaultMessage: "Text" });
    case "json":
      return intl.formatMessage({ id: "summary-dialog.format.json", defaultMessage: "JSON" });
    case "pdf":
    default:
      return intl.formatMessage({ id: "summary-dialog.format.pdf", defaultMessage: "PDF" });
  }
}

function downloadFormatMessage(formatId) {
  switch (formatId) {
    case "md":
      return <FormattedMessage id="summary-dialog.format.markdown" defaultMessage="Markdown" />;
    case "txt":
      return <FormattedMessage id="summary-dialog.format.text" defaultMessage="Text" />;
    case "json":
      return <FormattedMessage id="summary-dialog.format.json" defaultMessage="JSON" />;
    case "pdf":
    default:
      return <FormattedMessage id="summary-dialog.format.pdf" defaultMessage="PDF" />;
  }
}

function minDatetimeLocalValue(firstValue, secondValue) {
  if (!firstValue) {
    return secondValue || "";
  }

  if (!secondValue) {
    return firstValue;
  }

  return firstValue < secondValue ? firstValue : secondValue;
}

export function SummaryGenerationDialog({
  initialPresetId = DEFAULT_SUMMARY_TIMEFRAME_PRESET,
  open,
  onOpenChange,
  onGenerate,
  isGenerating = false,
  summaryDataSources = DEFAULT_SUMMARY_DATA_SOURCES
}) {
  const intl = useIntl();
  const classes = useStyles();
  const allowedDataSources = useMemo(() => sanitizeSummaryDataSources(summaryDataSources), [summaryDataSources]);
  const [presetId, setPresetId] = useState(initialPresetId);
  const [downloadFormat, setDownloadFormat] = useState(DEFAULT_SUMMARY_DOWNLOAD_FORMAT);
  const [selectedDataSources, setSelectedDataSources] = useState(() => allowedDataSources);
  const [customRange, setCustomRange] = useState(() => getDefaultCustomTimeframe());
  const [validationMessage, setValidationMessage] = useState("");
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!open) {
      setPresetId(initialPresetId);
      setDownloadFormat(DEFAULT_SUMMARY_DOWNLOAD_FORMAT);
      setSelectedDataSources(allowedDataSources);
      setCustomRange(getDefaultCustomTimeframe());
      setValidationMessage("");
      setSubmitError("");
    }
  }, [allowedDataSources, initialPresetId, open]);

  const selectedTimeframe = useMemo(
    () =>
      buildSummaryTimeframe({
        presetId,
        customFrom: customRange.from,
        customTo: customRange.to
      }),
    [customRange.from, customRange.to, presetId]
  );
  const customSelected = presetId === "custom";
  const selectedPresetLabel = presetLabel(intl, presetId);
  const selectedDownloadFormatLabel = downloadFormatLabel(intl, downloadFormat);
  const selectedDataSourcesLabel = selectedDataSources.map(sourceId => dataSourceLabel(intl, sourceId)).join(", ");
  const currentDatetimeMax = toDatetimeLocalValue(new Date());
  const customFromMax = minDatetimeLocalValue(customRange.to, currentDatetimeMax);
  const closeDialog = () => onOpenChange?.(null, { open: false });

  const handleGenerate = async () => {
    setSubmitError("");
    if (!selectedTimeframe.valid) {
      setValidationMessage(
        intl.formatMessage({
          id: "summary-dialog.validation.timeframe",
          defaultMessage: "Select a valid time range."
        })
      );
      return;
    }

    setValidationMessage("");
    try {
      await onGenerate({
        from: selectedTimeframe.from,
        to: selectedTimeframe.to,
        dataSources: sanitizeSummaryDataSources(selectedDataSources, allowedDataSources),
        downloadFormat
      });
      closeDialog();
    } catch (error) {
      setSubmitError(
        intl.formatMessage({
          id: "summary-dialog.validation.generate-failed",
          defaultMessage: "Summary generation failed."
        })
      );
    }
  };

  const handleCustomChange = field => event => {
    const { value } = event.target;
    setCustomRange(currentRange => ({
      ...currentRange,
      [field]: value
    }));
    setValidationMessage("");
    setSubmitError("");
  };

  const handlePresetSelect = (_, data) => {
    if (data.optionValue) {
      setPresetId(data.optionValue);
    }
    setValidationMessage("");
    setSubmitError("");
  };

  const handleFormatSelect = (_, data) => {
    if (data.optionValue) {
      setDownloadFormat(data.optionValue);
    }
    setValidationMessage("");
    setSubmitError("");
  };

  const handleDataSourcesSelect = (_, data) => {
    setSelectedDataSources(sanitizeSummaryDataSources(data.selectedOptions, allowedDataSources));
    setSubmitError("");
  };

  const timeframeLabel = intl.formatMessage({ id: "summary-dialog.timeframe", defaultMessage: "Time range" });
  const formatLabel = intl.formatMessage({ id: "summary-dialog.format", defaultMessage: "File format" });
  const dataSourcesLabel = intl.formatMessage({ id: "summary-dialog.data-sources", defaultMessage: "Data sources" });

  return (
    <Dialog modalType="alert" open={open} onOpenChange={onOpenChange}>
      <DialogSurface className={classes.dialog}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label={intl.formatMessage({ id: "room-settings-sidebar.close-button", defaultMessage: "Close" })}
                icon={<Dismiss24Regular />}
                onClick={closeDialog}
              />
            }
          >
            <FormattedMessage id="summary-dialog.title" defaultMessage="Generate Summary" />
          </DialogTitle>
          <DialogContent>
            <Field
              className={classes.timeframeField}
              label={<FormattedMessage id="summary-dialog.timeframe" defaultMessage="Time range" />}
            >
              <Dropdown
                aria-label={timeframeLabel}
                appearance="filled-darker"
                className={classes.fullWidthControl}
                disabled={isGenerating}
                onOptionSelect={handlePresetSelect}
                selectedOptions={[presetId]}
                value={selectedPresetLabel}
              >
                {SUMMARY_TIMEFRAME_PRESETS.map(preset => (
                  <Option key={preset.id} text={presetLabel(intl, preset.id)} value={preset.id}>
                    {presetMessage(preset.id)}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            {customSelected && (
              <div className={classes.customFields}>
                <Field
                  className={classes.customDateTimeField}
                  label={<FormattedMessage id="summary-dialog.from" defaultMessage="From" />}
                  validationState={validationMessage ? "error" : "none"}
                >
                  <Input
                    appearance="filled-darker"
                    className={classes.dateTimeInput}
                    disabled={isGenerating}
                    max={customFromMax || undefined}
                    type="datetime-local"
                    value={customRange.from}
                    onChange={handleCustomChange("from")}
                  />
                </Field>
                <Field
                  className={classes.customDateTimeField}
                  label={<FormattedMessage id="summary-dialog.to" defaultMessage="To" />}
                  validationState={validationMessage ? "error" : "none"}
                >
                  <Input
                    appearance="filled-darker"
                    className={classes.dateTimeInput}
                    disabled={isGenerating}
                    max={currentDatetimeMax || undefined}
                    min={customRange.from || undefined}
                    type="datetime-local"
                    value={customRange.to}
                    onChange={handleCustomChange("to")}
                  />
                </Field>
              </div>
            )}
            {customSelected && validationMessage && (
              <div className={classes.validationMessage} role="alert">
                <ErrorCircle12Filled aria-hidden="true" className={classes.validationIcon} />
                <span>{validationMessage}</span>
              </div>
            )}
            <Field
              className={classes.timeframeField}
              label={<FormattedMessage id="summary-dialog.format" defaultMessage="File format" />}
            >
              <Dropdown
                aria-label={formatLabel}
                appearance="filled-darker"
                className={classes.fullWidthControl}
                disabled={isGenerating}
                onOptionSelect={handleFormatSelect}
                selectedOptions={[downloadFormat]}
                value={selectedDownloadFormatLabel}
              >
                {SUMMARY_DOWNLOAD_FORMATS.map(format => (
                  <Option key={format.id} text={downloadFormatLabel(intl, format.id)} value={format.id}>
                    {downloadFormatMessage(format.id)}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Field
              className={classes.timeframeField}
              label={<FormattedMessage id="summary-dialog.data-sources" defaultMessage="Data sources" />}
            >
              <Dropdown
                aria-label={dataSourcesLabel}
                appearance="filled-darker"
                className={classes.fullWidthControl}
                disabled={isGenerating}
                multiselect
                onOptionSelect={handleDataSourcesSelect}
                selectedOptions={selectedDataSources}
                value={selectedDataSourcesLabel}
              >
                {allowedDataSources.map(sourceId => (
                  <Option key={sourceId} text={dataSourceLabel(intl, sourceId)} value={sourceId}>
                    {dataSourceMessage(sourceId)}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            {submitError && (
              <Field validationMessage={submitError} validationState="error">
                <span />
              </Field>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={closeDialog} disabled={isGenerating}>
              <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
            </Button>
            <Button
              appearance="primary"
              icon={isGenerating ? <Spinner size="tiny" /> : undefined}
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <FormattedMessage id="summary-dialog.generating" defaultMessage="Generating" />
              ) : (
                <FormattedMessage id="summary-dialog.generate" defaultMessage="Generate" />
              )}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

SummaryGenerationDialog.propTypes = {
  initialPresetId: PropTypes.string,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  onGenerate: PropTypes.func.isRequired,
  isGenerating: PropTypes.bool,
  summaryDataSources: PropTypes.arrayOf(PropTypes.string)
};
